import React from 'react';
import styled from '@emotion/styled';
import {
  COLOR,
  FONT_WEIGHT,
  FONT_SIZE,
  BREAKPOINTS,
} from '@ordermentum/ui-static';
import { css } from '@emotion/core';
import Icon from '../../atoms/Icon';
import S from '../../foundations/size';

export type TooltipMessage = {
  id: string;
  defaultMessage?: string;
  description?: string;
};

type Props = {
  message: TooltipMessage;
  position?: 'static' | 'absolute';
};

type BaseProps = {
  position?: 'static' | 'absolute';
  toolTipPosition?: 'top' | 'bottom';
};

const BaseStyles = css(`
  label: tooltip;
  display: flex;
  position: relative;
  text-decoration: none !important;

  @media (max-width: ${BREAKPOINTS.TABLET_MIN}) {
    display: none;
  }

  &:hover {
    &:after {
      width: 13rem;
      content: attr(title);
      background: ${COLOR.GRAY_ALICE_BLUE};
      color: ${COLOR.GRAY};
      font-size: 0.75rem;
      font-weight: ${FONT_WEIGHT.MEDIUM};
      padding: 0.5rem 0.75rem;
      position: absolute;
      z-index: 9;
      white-space: pre-wrap;
    }
    &:before {
      border-left: solid transparent 0.3rem;
      border-right: solid transparent 0.3rem;
      content: '';
      position: absolute;
      z-index: 9;
    }
  }
`);

const Base = styled('a')<BaseProps>`
  ${BaseStyles}
  margin-left: ${p => (p.position === 'absolute' ? '0' : S.XXXS)};
  width: 1rem;
  &:hover {
    &:after {
      margin-bottom: 0.75rem;
      position: absolute;
      bottom: ${p =>
        p.position === 'absolute'
          ? `calc(100% + ${FONT_SIZE.SMALL})`
          : '0.625rem'};
      left: -5.5rem;
    }
    &:before {
      border-top: solid ${COLOR.GRAY_ALICE_BLUE} 0.55rem;
      left: ${p => (p.position === 'absolute' ? 'calc(50%)' : S.XXXS)};
      bottom: ${p => (p.position === 'absolute' ? S.M : S.S)};
    }
  }
`;

const iconStyles = (position: 'absolute' | 'static') => ({
  label: 'tooltipIcon',
  ':hover': {
    '& .om-icon-tooltip::before': {
      color: 'black',
    },
  },
  color: COLOR.GRAYISH,
  fontSize: '0.8rem',
  left: '0.5rem',
  cursor: 'pointer',
  position,
  top: `-1rem`,
  marginLeft: position === 'absolute' ? '0' : S.XXXS,
});

export const Tooltip = ({ message, position = 'absolute' }: Props) => {
  if (Object.keys(message).length === 0) return null;

  const title = message.defaultMessage ?? message.description;

  return (
    <Base title={title} position={position} role="tooltip">
      {/* Note(nesa):
        We need an empty title attribute to trick the browser in
        showing this one instead of the one in the a tag
      */}
      <span title="">
        <Icon name="tooltip" size="x3s" modifiers={iconStyles(position)} />
      </span>
    </Base>
  );
};

export default Tooltip;
